import { Check, Copy } from "lucide-react";
import { useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { fullScreenOnMobile } from "../layout/mobileDialog";

/**
 * Shows a freshly generated temporary password to an administrator exactly
 * once, with a copy button. Used after creating a user and after resetting a
 * password.
 */
export const TemporaryPasswordDialog = ({
  open,
  name,
  password,
  onClose,
}: {
  open: boolean;
  name: string;
  password: string;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be denied (e.g. insecure context); the password
      // stays selectable in the input.
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className={`md:max-w-md ${fullScreenOnMobile}`}>
        <DialogHeader>
          <DialogTitle>
            {translate("resources.sales.temporary_password.title", { name })}
          </DialogTitle>
          <DialogDescription>
            {translate("resources.sales.temporary_password.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="temporary-password">
            {translate("resources.sales.temporary_password.label")}
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="temporary-password"
              value={password}
              readOnly
              className="font-mono tracking-wider"
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleCopy}
              aria-label={translate(
                copied ? "crm.common.copied" : "crm.common.copy",
              )}
            >
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            {translate("ra.action.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
